<template>
  <ODialog ref="dialog" hideWhenLoading :maxWidth="600">
    <v-card>
      <v-window :value="currentTab">
        <v-window-item :value="1">
          <ODialogBar
            title="Escolha o tipo do mailbox a ser criado"
            @close="() => $refs.dialog.$dismiss()"
          />

          <v-card-text class="pa-6">
            <v-text-field
              v-model="txtFilter"
              placeholder="Filtrar..."
              single-line
              hide-details
            />
          </v-card-text>

          <div class="maxh-4">
            <v-list class="py-0" v-if="fromClipboard != null">
              <v-list-item
                ripple
                class="yellow lighten-4"
                @click="selectFromClipboard"
              >
                <v-list-item-content class="px-2">
                  <ORow left>
                    <v-icon class="mr-2">mdi-clipboard-outline</v-icon>
                    {{ fromClipboardLabel }}
                  </ORow>
                </v-list-item-content>
                <v-list-item-action class="px-2">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <OSelectList
              :rows="filteredTypes"
              labelProp="label"
              @rowClicked="selectType"
            />
          </div>
        </v-window-item>

        <v-window-item :value="2">
          <ODialogBar
            :title="`Novo Mailbox: ${selectedType.label}`"
            @close="() => $refs.dialog.$dismiss()"
          />

          <v-card-text class="pa-6">
            <v-text-field
              v-model="newItem.label"
              label="Comentários"
              placeholder="Descreva a necessidade deste mailbox"
            />
            <v-textarea
              class="monospace-input"
              v-model="newItem.contents"
              rows="10"
              single-line
              hide-details
            />
          </v-card-text>
          <ODialogButtons
            class="pa-6"
            txtApply="Criar"
            @cancel="() => $refs.dialog.$dismiss()"
            @apply="save"
          />
        </v-window-item>

        <v-window-item :value="3">
          <ODialogBar
            title="Novos Mailboxes"
            @close="() => $refs.dialog.$dismiss()"
          />

          <v-simple-table dense class="py-6">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Tipo ─ Label</th>
                  <th class="text-right">Endereço (dec)</th>
                  <th class="text-right">Qtde. Bytes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in fromClipboard" :key="idx">
                  <td>
                    <code>{{ row.information }}</code>
                    {{ row.label }}
                  </td>
                  <td class="text-right">
                    <code>{{ row.contents.address }}</code>
                  </td>
                  <td class="text-right">{{ byteLen(row.contents.bytes) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <ODialogButtons
            class="pa-6"
            txtApply="Criar"
            @cancel="() => $refs.dialog.$dismiss()"
            @apply="save"
          />
        </v-window-item>
      </v-window>
    </v-card>
  </ODialog>
</template>

<script>
import { samples } from './Informations'
import { matchAnyLike, jp, isString } from 'oderon-commons/src/utils'
import OSelectList from 'oderon-vue/cpts/OSelectList'
import { MailboxItem } from '@/models'

function getItems(str) {
  try {
    let obj = JSON.parse(str)
    if (Array.isArray(obj)) {
      let result = obj.map((x) => getOneItem(x)).filter((x) => x != null)
      return result.length ? result : null
    } else {
      return getOneItem(obj)
    }
  } catch (err) {
    console.log('Error reading from clipboard, will ignore', err.message)
    return null
  }
}

function getOneItem(x) {
  let { label, information, contents, address, bytes } = x
  if (contents == null) contents = x.originJson

  // Se não tem contents, mas tem address e bytes, aproveita.
  if (contents == null && address != null && bytes != null) {
    contents = { address, bytes }
  }

  if (contents == null) return null

  if (information == null) information = 'raw'
  if (label == null) label = 'Mailbox do clipboard'
  return { label, information, contents }
}

export default {
  components: { OSelectList },
  props: {
    client: { required: true }
  },
  data() {
    return {
      txtFilter: null,
      currentTab: 1,
      selectedType: {},
      newItem: {},

      fromClipboard: null
    }
  },
  computed: {
    filteredTypes() {
      return samples.filter((t) => {
        return matchAnyLike(this.txtFilter, t.label, t.value)
      })
    },
    fromClipboardLabel() {
      if (this.fromClipboard == null) return null
      if (Array.isArray(this.fromClipboard))
        return this.fromClipboard.length + ' mailboxes do clipboard'
      return this.fromClipboard.label || 'Mailbox do clipboard'
    }
  },
  methods: {
    open() {
      this.txtFilter = null
      this.currentTab = 1
      this.tryClipboard()
      return this.$refs.dialog.$open()
    },

    async tryClipboard() {
      this.fromClipboard = null
      try {
        let str = await navigator.clipboard.readText()
        this.fromClipboard = getItems(str)
      } catch (err) {
        // noop
      }
    },

    selectFromClipboard() {
      if (Array.isArray(this.fromClipboard)) {
        this.currentTab = 3
        this.newItem = this.fromClipboard
      } else {
        this.selectedType = samples.find(
          (x) => x.value == this.fromClipboard.information
        )
        this.currentTab = 2
        let { label, information, contents } = this.fromClipboard
        this.newItem = { label, information, contents: jp(contents) }
      }
    },

    byteLen(str) {
      if (str == null) return '─'
      return Math.ceil(str.replace(/\s+/g, '').length / 2)
    },

    selectType(t) {
      this.selectedType = t
      this.newItem = {
        label: null,
        information: t.value,
        contents: jp(t.sample || {})
      }

      this.currentTab = 2
    },

    save() {
      this.$refs.dialog.$loading(async () => {
        let cc = [this.client.id]

        let items = this.newItem
        if (!Array.isArray(items)) items = [items]

        for (let item of items) {
          let contents = item.contents
          if (isString(contents)) contents = JSON.parse(contents)

          let payload = {
            cc,
            label: item.label,
            information: item.information,
            contents
          }

          await MailboxItem.save(payload)
        }

        let msg = items.length + ' mailboxes criados com sucesso'
        if (items.length == 1) msg = 'Mailbox criado com sucesso'

        this.$snack(msg)
        this.$refs.dialog.$dismiss()
        this.$emit('reload')
      })
    }
  }
}
</script>

<style></style>
